import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useDropzone } from "react-dropzone";
import { useApi } from "../../hooks/useApi";
import { formatFileSize } from "../../utils/formatFileSize";
import {
  IMAGE_UPLOAD_ACCEPT,
  StorefrontStaticAsset,
  StorefrontStaticAssetKind,
} from "../../types/evidence";

dayjs.extend(utc);

export interface AssetSpec {
  kind: StorefrontStaticAssetKind;
  title: string;
  description: string;
  emptyHint: string;
}

export const ASSETS: AssetSpec[] = [
  {
    kind: "logo",
    title: "Logo",
    description: "Printed in the footer of every document.",
    emptyHint: "Drop the logo here",
  },
  {
    kind: "checkout",
    title: "Checkout screenshot",
    description: "Printed in the Screenshots section of every dispute. An upload on a dispute overrides it for that case.",
    emptyHint: "Drop the checkout page as the customer saw it",
  },
  {
    kind: "account",
    title: "Account / cancel page",
    description: "Printed in Proof of Digital Delivery and Usage of every dispute on a digital storefront.",
    emptyHint: "Drop the account page with its Cancel Subscription link",
  },
];

export const CONTROL_WIDTH = 300;

function fileLine(asset: StorefrontStaticAsset): string | null {
  if (asset.sizeBytes === undefined || !asset.uploadedAt) return null;
  const name = asset.fileName ?? (asset.contentType === "image/jpeg" ? "JPEG" : "PNG");
  return `${name} · ${formatFileSize(asset.sizeBytes)} · uploaded ${dayjs.utc(asset.uploadedAt).format("MMM D")}`;
}

// An <img src> sends no Authorization header, so the bytes come through useApi and live as an
// object URL for as long as the control shows that asset.
function useAssetThumbnail(storefrontId: number, kind: StorefrontStaticAssetKind, assetKey: string | null) {
  const { getBlob } = useApi();
  const [url, setUrl] = useState<string | null>(null);
  // A fetch that failed must not read as one still running, or the tile loads forever.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!assetKey) {
      setUrl(null);
      setFailed(false);
      return;
    }
    let objectUrl: string | null = null;
    let dropped = false;
    setFailed(false);
    // A replaced asset keeps the old key's object URL until the new bytes arrive, and that URL is
    // revoked by this effect's cleanup, so the tile would show a dead image instead of loading.
    setUrl(null);
    getBlob(`/api/storefronts/${storefrontId}/static-assets/${kind}`, { slowApi: true })
      .then((blob) => {
        if (dropped) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (!dropped) { setUrl(null); setFailed(true); } });

    return () => {
      dropped = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [getBlob, storefrontId, kind, assetKey]);

  return { url, failed };
}

export default function StorefrontAssetControl({ spec, storefrontId, asset, busy, disabled, onUpload, onRefuse, onRemove }: {
  spec: AssetSpec;
  storefrontId: number;
  asset: StorefrontStaticAsset | undefined;
  busy: boolean;
  disabled: boolean;
  onUpload: (file: File) => void;
  onRefuse: (message: string) => void;
  onRemove: () => void;
}) {
  const { url: thumbnail, failed: thumbnailFailed } = useAssetThumbnail(storefrontId, spec.kind, asset?.key ?? null);
  const line = asset ? fileLine(asset) : null;
  const locked = disabled || busy;
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // noClick: the preview is the click target, so the buttons under it are not buttons inside a button.
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: Object.fromEntries(IMAGE_UPLOAD_ACCEPT.split(",").map((type) => [type, []])),
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: locked,
    // A wrong-type rejection goes through the same handler: imageUploadError words the refusal
    // the API would give. Several files at once have no single file to validate.
    onDrop: (accepted, rejections) => {
      if (rejections.some((rejection) => rejection.errors.some((error) => error.code === "too-many-files"))) {
        onRefuse("Drop one file at a time");
        return;
      }
      const file = accepted[0] ?? rejections[0]?.file;
      if (file) onUpload(file);
    },
  });

  return (
    <Box {...getRootProps()} sx={{ width: CONTROL_WIDTH }}>
      <input {...getInputProps({ "aria-label": `${asset ? "Replace" : "Upload"} ${spec.title}`, disabled: locked })} />
      <Box
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-label={`${spec.title}: drop an image or click to choose one`}
        onClick={locked ? undefined : open}
        onKeyDown={(event) => { if (!locked && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); open(); } }}
        sx={{
          height: 96,
          borderRadius: 1,
          border: asset ? "1px solid" : "1px dashed",
          borderColor: isDragActive ? "primary.main" : "divider",
          backgroundColor: isDragActive || asset ? "action.hover" : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center",
          overflow: "hidden", px: 1,
          cursor: locked ? "default" : "pointer",
          "&:hover": { borderColor: locked ? "divider" : "primary.main" },
        }}
      >
        {asset
          ? (thumbnail
            ? <Box component="img" src={thumbnail} alt={spec.title} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            : (
              <Typography variant="caption" color="text.secondary">
                {thumbnailFailed ? "Preview unavailable" : "Loading preview…"}
              </Typography>
            ))
          : <Typography variant="caption" color="text.secondary">{spec.emptyHint}</Typography>}
      </Box>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, ml: -1 }}>
        <Button
          size="small"
          disabled={locked}
          startIcon={busy ? <CircularProgress size={14} /> : null}
          onClick={open}
        >
          {asset ? "Replace" : "Upload"}
        </Button>
        {asset && thumbnail && (
          <Button size="small" color="inherit" component="a" href={thumbnail} target="_blank" rel="noopener" sx={{ color: "text.secondary" }}>
            View
          </Button>
        )}
        {asset && (
          <Button
            size="small"
            color="inherit"
            aria-label={`Remove ${spec.title}`}
            disabled={locked}
            onClick={() => setConfirmingRemove(true)}
            sx={{ color: "text.secondary" }}
          >
            Remove
          </Button>
        )}
      </Stack>
      {line && (
        <Typography variant="caption" color="text.secondary" component="p" noWrap>{line}</Typography>
      )}
      <Dialog
        open={confirmingRemove}
        onClose={(_, reason) => { if (reason === "backdropClick") return; setConfirmingRemove(false); }}
      >
        <DialogTitle>{`Remove the ${spec.title.toLowerCase()}?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>Documents generated later print without it.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingRemove(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => { setConfirmingRemove(false); onRemove(); }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
